// src/Transcribe.js
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { MicrophoneIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon, Bars3Icon } from "@heroicons/react/20/solid";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';

const StreamingPoint = process.env.REACT_APP_API_STREAMING;
const EndPoint = process.env.REACT_APP_API_ENDPOINT;

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

class Transcribe extends React.Component {
  state = {
    isRecording: false,
    status: "idle",
    sessionId: uuidv4(),
    transcripts: [],
    currentMediaRecorder: null,
    showSidebar: false,
    showTooltip: false,
    livePreview: "",
    // Draggable state
    position: { x: window.innerWidth - 380, y: 80 },
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    // Accordion state
    accordionOpen: true,
    sidebarWidth: 360,
    recordingStartTime: null,
    recordingDuration: 0,
  };

  static defaultProps = {
    onInputUpdate: () => {},
  };

  stream = null;
  mediaRecorder = null;
  ws = null;
  sentToParentOnce = false;
  recordingEnded = false;
  finalTranscriptCache = "";
  sidebarRef = React.createRef();

  componentDidMount() {
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("touchmove", this.onTouchMove, { passive: false });
    window.addEventListener("touchend", this.onMouseUp);
  }

  componentWillUnmount() {
    this.stop(false);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("touchend", this.onMouseUp);
  }

  // ── Drag handlers ──────────────────────────────────────────────────────────
  onDragStart = (e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    this.setState({
      isDragging: true,
      dragOffset: {
        x: clientX - this.state.position.x,
        y: clientY - this.state.position.y,
      },
    });
  };

  onMouseMove = (e) => {
    if (!this.state.isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const newX = Math.max(0, Math.min(clientX - this.state.dragOffset.x, window.innerWidth - this.state.sidebarWidth));
    const newY = Math.max(0, Math.min(clientY - this.state.dragOffset.y, window.innerHeight - 60));
    this.setState({ position: { x: newX, y: newY } });
  };

  onTouchMove = (e) => {
    if (this.state.isDragging) e.preventDefault();
    this.onMouseMove(e);
  };

  onMouseUp = () => {
    this.setState({ isDragging: false });
  };

  // ── WebSocket & recording ───────────────────────────────────────────────────
  getWebSocketUrl() {
    if (StreamingPoint) return StreamingPoint;
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    return isLocal ? "ws://localhost:4000/transcribe" : `wss://${window.location.host}/transcribe`;
  }

  start = async () => {
    if (this.state.isRecording) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setState({ status: "getUserMedia not supported" });
      toast.error("Microphone not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.stream = stream;
      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        toast.info("Recording started…", { autoClose: 1000 });
        this.sentToParentOnce = false;
        this.recordingEnded = false;
        this.finalTranscriptCache = "";
        this.setState({ isRecording: true, status: "recording", livePreview: "", showSidebar: true, recordingStartTime: Date.now(), transcripts: [], recordingDuration: 0 }, () => {
          this.startRecorder();

          // start timer
          this.timer = setInterval(() => {
            const durationMs = Date.now() - this.state.recordingStartTime;
            this.setState({
              recordingDuration: Math.floor(durationMs / 1000),
            });
          }, 1000);
        });
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "transcript") {
            const { text, isFinal } = msg;
            if (!isFinal) {
              this.setState({ livePreview: text });
              return;
            }
            this.setState((prev) => ({
              livePreview: "",
              transcripts: [...prev.transcripts, { sequence: prev.transcripts.length + 1, text, jobName: "streaming" }],
            }));
          } else if (msg.type === "error") {
            this.setState({ status: "transcribe error" });
            toast.error("Transcription error.");
          }
        } catch (e) {
          console.error("WS parse error:", e);
        }
      };

      this.ws.onerror = () => {
        this.setState({ status: "WebSocket error" });
        toast.error("WebSocket connection error.");
      };

      this.ws.onclose = () => {
        this.recordingEnded = true;
        this.finalTranscriptCache = this.combinedTranscript();
        if (!this.state.showSidebar) this.sendFinalToParentOnce();
        this.ws = null;
      };
    } catch (err) {
      this.setState({ status: "mic permission denied or unsupported" });
      toast.error("Microphone permission denied.");
    }
  };

  startRecorder = () => {
    if (!this.stream || !this.state.isRecording) return;
    try {
      this.mediaRecorder = new MediaRecorder(this.stream);
    } catch (e) {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
      return;
    }
    this.setState({ currentMediaRecorder: this.mediaRecorder });

    this.mediaRecorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      e.data.arrayBuffer().then((buf) => {
        try { this.ws.send(buf); } catch (err) { console.error("WS send error:", err); }
      });
    };

    this.mediaRecorder.onerror = () => {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
    };

    this.mediaRecorder.onstop = () => this.setState({ currentMediaRecorder: null });
    this.mediaRecorder.start(250);
  };

  stop = (showSidebar = true) => {
    if (!this.state.isRecording && !this.stream && !this.ws) return;
    this.setState({ isRecording: false, status: "stopped", showSidebar: showSidebar ? true : this.state.showSidebar });

    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") this.mediaRecorder.stop();
    if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify({ type: "end" })); } catch (e) {}
      setTimeout(() => { if (this.ws) { this.ws.close(); this.ws = null; } }, 500);
    } else if (this.ws) { this.ws.close(); this.ws = null; }

    const durationMs = Date.now() - this.state.recordingStartTime;
    const durationSec = Math.floor(durationMs / 1000);

    this.setState({
      recordingDuration: durationSec,
    });

    toast.info("Recording stopped.", { autoClose: 1000 });
  };

  sendFinalToParentOnce = async () => {
    if (this.sentToParentOnce) return;
    const full = (this.finalTranscriptCache || this.combinedTranscript()).trim();
    if (!full) return;
    this.sentToParentOnce = true;

    const res = await axios.post(`${EndPoint}/careplan/transcribe-credit`,{duration: this.state.recordingDuration}, { withCredentials: true });
    const output = res.data.output;
    this.props.onInputUpdate({ transcription: full, output});
  };

  toggleRecording = () => {
    if (this.state.isRecording) this.stop(true);
    else this.start();
  };

  combinedTranscript() {
    return this.state.transcripts
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((t) => t.text)
      .join(" ")
      .trim();
  }

  // ── Sidebar render ──────────────────────────────────────────────────────────
  renderSidebar() {
    const { showSidebar, isRecording, currentMediaRecorder, livePreview, transcripts, status, position, isDragging, accordionOpen, sidebarWidth } = this.state;
    if (!showSidebar) return null;

    const transcriptText = (this.combinedTranscript() + (livePreview ? " " + livePreview : "")).trim();
    const segmentCount = transcripts.length;

    return (
      <div
        ref={this.sidebarRef}
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          width: sidebarWidth,
          zIndex: 9999,
          cursor: isDragging ? "grabbing" : "default",
          userSelect: "none",
          filter: "drop-shadow(0 8px 32px rgba(12,74,110,0.18))",
        }}
      >
        {/* ── Panel ── */}
        <div style={{
          borderRadius: 20,
          overflow: "hidden",
          border: "1.5px solid #bae6fd",
          background: "#fff",
          boxShadow: "0 4px 32px 0 rgba(12,74,110,0.10)",
        }}>

          {/* ── Drag handle / header ── */}
          <div
            onMouseDown={this.onDragStart}
            onTouchStart={this.onDragStart}
            style={{
              background: "linear-gradient(135deg, #0c4a6e 0%, #0369a1 60%, #0e7490 100%)",
              padding: "12px 16px",
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Bars3Icon style={{ width: 16, height: 16, color: "rgba(255,255,255,0.55)", flexShrink: 0 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Pulse dot */}
                <div style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
                  <div style={{
                    position: "absolute", inset: 0, borderRadius: "50%",
                    background: isRecording ? "#f87171" : "#94a3b8",
                    animation: isRecording ? "transcribePulse 1.2s ease-in-out infinite" : "none",
                  }} />
                  {isRecording && (
                    <div style={{
                      position: "absolute", inset: -3, borderRadius: "50%",
                      border: "2px solid rgba(248,113,113,0.45)",
                      animation: "transcribeRing 1.2s ease-in-out infinite",
                    }} />
                  )}
                </div>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: "0.01em" }}>
                  Live Transcription
                </span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                background: isRecording ? "rgba(239,68,68,0.22)" : "rgba(255,255,255,0.12)",
                color: isRecording ? "#fca5a5" : "rgba(255,255,255,0.65)",
                borderRadius: 100, padding: "2px 10px", fontSize: 10,
                fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              }}>
                {isRecording ? "REC" : "Stopped"}
              </span>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  if (isRecording) this.stop(false);
                  this.setState({ showSidebar: false }, () => {
                    if (this.recordingEnded) this.sendFinalToParentOnce();
                  });
                }}
                style={{
                  background: "rgba(255,255,255,0.12)", border: "none",
                  borderRadius: 8, padding: "4px 6px", cursor: "pointer",
                  display: "flex", alignItems: "center", color: "rgba(255,255,255,0.75)",
                  transition: "background 0.15s",
                }}
                title="Close"
              >
                <XMarkIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>

          {/* ── Controls ── */}
          <div style={{ padding: "12px 14px 0", background: "#f0f9ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={this.toggleRecording}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: isRecording
                    ? "linear-gradient(135deg, #dc2626, #ef4444)"
                    : "linear-gradient(135deg, #0c4a6e, #0369a1)",
                  border: "none", borderRadius: 12, padding: "8px 14px",
                  color: "#fff", fontWeight: 700, fontSize: 12,
                  cursor: "pointer", boxShadow: isRecording
                    ? "0 2px 12px rgba(220,38,38,0.35)"
                    : "0 2px 12px rgba(12,74,110,0.25)",
                  transition: "all 0.2s", flexShrink: 0,
                }}
              >
                <MicrophoneIcon style={{ width: 15, height: 15 }} />
                {isRecording ? "Stop" : "Record"}
              </button>

              {/* Visualizer */}
              <div style={{
                flex: 1, background: "#fff", borderRadius: 10,
                border: "1px solid #e0f2fe", padding: "4px 8px",
                display: "flex", alignItems: "center", minWidth: 0,
                height: 42,
              }}>
                {isRecording && currentMediaRecorder ? (
                  <LiveAudioVisualizer
                    mediaRecorder={currentMediaRecorder}
                    width={180} height={32}
                    barWidth={2} gap={1.5}
                    barColor={"#0369a1"}
                  />
                ) : (
                  <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                    Waiting for audio…
                  </span>
                )}
              </div>
            </div>

            {/* Status row */}
            <div style={{ display: "flex", gap: 6, marginTop: 8, marginBottom: 10, flexWrap: "wrap" }}>
              <span style={chipStyle("#e0f2fe", "#0369a1")}>{status}</span>
              <span style={chipStyle("#e0f2fe", "#0369a1")}>{segmentCount} segment{segmentCount !== 1 ? "s" : ""}</span>
              <span style={chipStyle("#e0f2fe", "#0369a1")}>
                ⏱ {formatTime(this.state.recordingDuration)}
              </span>
              {livePreview && <span style={chipStyle("#fef3c7", "#92400e")}>live preview</span>}
            </div>
          </div>

          {/* ── Accordion: Transcript ── */}
          <div style={{ borderTop: "1px solid #e0f2fe" }}>
            {/* Accordion header */}
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => this.setState((s) => ({ accordionOpen: !s.accordionOpen }))}
              style={{
                width: "100%", display: "flex", alignItems: "center",
                justifyContent: "space-between", padding: "10px 14px",
                background: "#f8fafc", border: "none", cursor: "pointer",
                borderBottom: accordionOpen ? "1px solid #e0f2fe" : "none",
                transition: "background 0.15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#0c4a6e", letterSpacing: "0.02em" }}>
                  TRANSCRIPT
                </span>
                {transcriptText && (
                  <span style={{
                    background: "#0c4a6e", color: "#fff",
                    borderRadius: 100, fontSize: 10, fontWeight: 700,
                    padding: "1px 7px",
                  }}>
                    {transcriptText.split(" ").filter(Boolean).length}w
                  </span>
                )}
              </div>
              {accordionOpen
                ? <ChevronUpIcon style={{ width: 15, height: 15, color: "#64748b" }} />
                : <ChevronDownIcon style={{ width: 15, height: 15, color: "#64748b" }} />
              }
            </button>

            {/* Accordion body */}
            {accordionOpen && (
              <div style={{ padding: "10px 14px 14px" }}>
                <textarea
                  onMouseDown={(e) => e.stopPropagation()}
                  readOnly
                  value={transcriptText}
                  placeholder="Transcript will appear here as you speak…"
                  style={{
                    width: "100%", minHeight: 220, maxHeight: 420,
                    resize: "vertical", borderRadius: 12,
                    border: "1.5px solid #e0f2fe", background: "#f8fafc",
                    padding: "10px 12px", fontSize: 13, lineHeight: 1.65,
                    color: "#1e3a5f", outline: "none", fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                />

                {/* Finish button */}
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    if (isRecording) this.stop(false);
                    this.setState({ showSidebar: false }, () => {
                      if (this.recordingEnded) this.sendFinalToParentOnce();
                    });
                  }}
                  style={{
                    marginTop: 10, width: "100%",
                    background: "linear-gradient(135deg, #0c4a6e 0%, #0369a1 100%)",
                    border: "none", borderRadius: 12, padding: "10px",
                    color: "#fff", fontWeight: 700, fontSize: 13,
                    cursor: "pointer", letterSpacing: "0.02em",
                    boxShadow: "0 2px 12px rgba(12,74,110,0.22)",
                    transition: "opacity 0.15s",
                  }}
                >
                  Finish & Send
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Keyframes injected once ── */}
        <style>{`
          @keyframes transcribePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(0.85); }
          }
          @keyframes transcribeRing {
            0% { opacity: 0.8; transform: scale(0.9); }
            100% { opacity: 0; transform: scale(1.8); }
          }
        `}</style>
      </div>
    );
  }

  render() {
    const { isRecording, showTooltip } = this.state;

    return (
      <>
        {/* ── Trigger button (always visible) ── */}
        <div className="flex items-center justify-center">
          <div className="relative flex items-center">
            <button
              id="microphone"
              disabled={isRecording}
              className={`group relative flex items-center rounded-2xl px-3 py-3 text-white shadow-md transition ${
                isRecording
                  ? "bg-slate-400 cursor-not-allowed opacity-60"
                  : "bg-sky-800 hover:bg-sky-900 cursor-pointer"
              }`}
              onClick={() => {
                if (isRecording) return;
                if (!this.state.showSidebar) {
                  this.setState({ showSidebar: true });
                }
                this.toggleRecording();
              }}
              onMouseEnter={() => this.setState({ showTooltip: true })}
              onMouseLeave={() => this.setState({ showTooltip: false })}
            >
              <MicrophoneIcon className="h-8 w-8" />
            </button>

            {showTooltip && (
              <div className="absolute left-1/2 top-[-48px] z-10 -translate-x-1/2 whitespace-nowrap rounded-xl bg-slate-800 px-3 py-2 text-xs text-white shadow-lg">
                {isRecording ? "Use the sidebar to stop recording" : "Voice recording"}
              </div>
            )}
          </div>
        </div>

        {this.renderSidebar()}
        <ToastContainer />
      </>
    );
  }
}

// ── Chip helper ────────────────────────────────────────────────────────────────
function chipStyle(bg, color) {
  return {
    background: bg, color, borderRadius: 100,
    padding: "2px 9px", fontSize: 10, fontWeight: 600,
    letterSpacing: "0.04em",
  };
}

export default Transcribe;